/**
 * deriveJourneyStep: the ONE decision behind "what is the learner's next move".
 *
 * EXTRACTED, NOT REWRITTEN. The body below is the Dashboard step cascade
 * (Dashboard.jsx @822fed3, the effect at :1477-1705) with two mechanical
 * changes and no others:
 *
 *   1. `dispatch(x)` becomes an entry appended to `sideEffects`. The CALLER
 *      dispatches them, in order. Order is load bearing: the cascade interleaves
 *      writes with reads, and one branch (the v4 "deliverable already submitted"
 *      shortcut) dispatches updateIsTaskCreated(true) and then reads the OLD
 *      mission.isTaskCreated in the SAME pass, so that pass still lands on Plan
 *      and only the NEXT render moves on. Applying the effect inside this
 *      function would silently collapse two renders into one and change what a
 *      student sees.
 *   2. Values the original read off objects (`capsule?.modules[last]?.is_completed`,
 *      `v4CapsuleUser?.milestone_report_submitted`) arrive as normalized
 *      booleans. Every original test was a truthiness test or `x === null` on a
 *      value already covered by `!x`, so the normalization is meaning-preserving;
 *      the harness proves it against a golden record taken from the ORIGINAL
 *      code before this file existed.
 *
 * WHAT THIS FUNCTION DOES NOT OWN. The credential terminal machinery
 * (credentialAdvancedRef, levelTransition, credentialTerminalDecision) stays in
 * Dashboard.jsx untouched. This function routes the DECISION, not the credential
 * level advance.
 *
 * SPRINT 3 CHIP C adds ONE identity and no new machinery. `exam` is the
 * credential state in which the deliverable is in AND the terminal final exam
 * beat is in play. It exists so the card stops saying "Your squad's work is in."
 * under a Final Exam node: that sentence binds to Ship, and only on a rail whose
 * Ship is the last thing. See `credentialExamState` below for why a LOCKED exam
 * is deliberately NOT this identity.
 *
 * THE CREDENTIAL SPINE IS AN INPUT, NOT A GAP. A credential cohort's redux
 * status is pinned at 1 forever by gate 1 (the credential mission path correctly
 * never writes capsule_user.read_objective), so the stock ladder is the WRONG
 * answer for those students even though the cascade is right to emit what it
 * emits. The side effects therefore stay stock; the decision comes from
 * deriveCredentialEffectiveStatus, passed in by the caller.
 *
 * W4-F2.10 ADDS ONE INPUT AND MOVES ONE GATE. `shipWalkthroughRecorded` is this
 * learner's own ship event for this capsule, and the Ship gate now asks for it
 * alongside the squad's `deliverables_submitted`. See `pastOwnShip` below for
 * what Wave 4 B2's roster-wide stamp did to the old single-fact gate. The
 * decision's vocabulary, its ladders and its side effects are untouched: the
 * same world reaches `terminal()` through the same branch, one render later than
 * the squad's stamp and exactly when the learner walks through Ship.
 *
 * ONE GATE, AND DELIBERATELY NOT TWO. The write-through shortcut two rungs
 * above Ship reads the squad's stamp on its own as well, and the 2026-09-19
 * domain review asked for the same pair there. Amir ruled it stays: "I was
 * actually okay with skipping the plan step if the deliverable is already
 * submitted. I like that skip... just not, obviously, the skip of the ship
 * step, because that's what triggers the beats." Addendum 5 rule 1 is about
 * the squad's output never advancing a learner OUT OF A LEVEL. Moving a
 * stamped squadmate past Plan and tasks to Check-in is inside the level and is
 * intended; the tests pin it as intended rather than tolerate it.
 */
import {
  BEAT_STATES,
  isSettledCleared,
} from "../../components/Dashboard/CredentialJourney/beatStates";
import { v4Gate1Decision } from "./v4Gate1Decision";

/** Semantic step identities. Stable across the v4/legacy transposition. */
export const JOURNEY_STEP = Object.freeze({
  NONE: "none",
  KICKOFF: "kickoff",
  MISSION: "mission",
  BRIEFINGS: "briefings",
  TAKE: "take",
  PLAN: "plan",
  WORK: "work",
  CHECKIN: "checkin",
  SHIP: "ship",
  SUBMITTED: "submitted",
  ACT1: "act1",
  EXAM: "exam",
});

/**
 * Is the credential's terminal final exam IN PLAY for this learner?
 *
 * Enumerated, never "anything but locked". `locked` is the state the backend
 * emits for a certificate-only mastery beat that is not yet ready, INCLUDING the
 * in-session lag right after the deliverable is submitted and before clearance
 * is refetched (ClearanceStateResolver::terminalExamGate; CredentialSpine.jsx's
 * refetch comment). `credentialRail` deliberately keeps the cursor on Ship for
 * exactly that state, so a locked-exam learner IS standing on a settled Ship and
 * reads the Ship sentence. Absent is the same answer for the same reason: a
 * capsule with no terminal exam is a plain submitted level.
 *
 * Written as an explicit set rather than `!== LOCKED` so a state the enum gains
 * later does not silently become "in play" and silence the sentence.
 *
 * @param {string|null|undefined} state a BEAT_STATES value, or null
 * @returns {boolean}
 */
export function credentialExamInPlay(state) {
  return (
    state === BEAT_STATES.ACTIVE ||
    isSettledCleared(state) ||
    state === BEAT_STATES.FAILED_TERMINAL
  );
}

/**
 * Is this decision a SETTLED journey? The one definition, named because both
 * `Dashboard.jsx` and the rails' tests need the same answer and a gate spelled
 * inline in two places is two answers waiting to disagree (the reasoning
 * terminalExamOpens is already written down for).
 *
 * A settled journey renders no interactive node: the level's work is in, and the
 * only two ways to be here are a submitted deliverable and a terminal exam beat
 * that is in play. The exam node's OWN interactivity is state derived and is not
 * this flag's business; see CredentialJourney's terminal branch.
 *
 * @param {string|null|undefined} stepId
 * @returns {boolean}
 */
export function journeyIsSettled(stepId) {
  return stepId === JOURNEY_STEP.SUBMITTED || stepId === JOURNEY_STEP.EXAM;
}

/** Side-effect descriptors. Mapped to real actions by journeySideEffectActions. */
export const JOURNEY_EFFECT = Object.freeze({
  SET_STATUS: "setStatus",
  SET_TASK_CREATED: "setTaskCreated",
  ADVANCE_CAPSULE: "advanceCapsule",
  FLAG_LAST_LEVEL: "flagLastLevel",
  /**
   * THE CARRY, RECORDED AT THE CARRY (W4-F2.11 fix batch, Codex MINOR 1).
   *
   * It carries NO VALUE, because the only thing it is about is the capsule the
   * decision is being made for and this function is not told which that is.
   * `Dashboard.jsx` supplies it from the same `capsuleId` the derivation was
   * run for, which is the shape `selectShipWalkthroughRecorded` already uses
   * for the other ledger-backed fact.
   *
   * IT IS NOT IN `journeySideEffectActions`. That mapping returns plain redux
   * actions, and recording this means writing the client ledger first and
   * mirroring the effective answer second, which is a thunk. An unknown
   * descriptor is already dropped there by design, so the golden record of the
   * decision keeps describing production exactly.
   */
  MARK_MOVED_FORWARD: "markMovedForward",
});

/**
 * status index -> semantic step, per ladder. THE TRANSPOSITION: v4 moved the
 * mission gate ahead of the lessons gate, so statuses 1..3 mean different steps
 * on the two ladders while 0 and 4..7 agree. Getting this backwards would attach
 * the wrong contextual sentence to the wrong node on 27 in-flight cohorts.
 */
const V4_STEP_BY_STATUS = Object.freeze([
  JOURNEY_STEP.KICKOFF,
  JOURNEY_STEP.MISSION,
  JOURNEY_STEP.BRIEFINGS,
  JOURNEY_STEP.TAKE,
  JOURNEY_STEP.PLAN,
  JOURNEY_STEP.WORK,
  JOURNEY_STEP.CHECKIN,
  JOURNEY_STEP.SHIP,
]);

const LEGACY_STEP_BY_STATUS = Object.freeze([
  JOURNEY_STEP.KICKOFF,
  JOURNEY_STEP.BRIEFINGS,
  JOURNEY_STEP.TAKE,
  JOURNEY_STEP.MISSION,
  JOURNEY_STEP.PLAN,
  JOURNEY_STEP.WORK,
  JOURNEY_STEP.CHECKIN,
  JOURNEY_STEP.SHIP,
]);

/**
 * The destination each step opens, verbatim from the ratified design's action
 * column (Design Wave 2 4.3). `null` where the design ratified no destination
 * name: kickoff and Mission are not Wave 2 return states, and Act 1 is ruled to
 * "use the current action's authored title", which this module does not own.
 *
 * These strings are contract data, not rendered copy. The ratified surface has
 * no re-entry button of any kind; the active journey node is the move.
 */
const LABEL_BY_DESTINATION = Object.freeze({
  kickoff: null,
  mission: null,
  briefings: "Continue Briefings",
  take: "Write my take",
  plan: "Open the plan",
  work: "Continue my task",
  checkin: "Start my check-in",
  ship: "Review what we're shipping",
  act1: null,
  // Chip C. No ratified destination name, and deliberately no new string: the
  // terminal exam node and the completion band below the card are the whole
  // story in this state, and the card renders no action beside them.
  exam: null,
  "back-to-challenge": "Back to the challenge",
  "next-level": "See the next level",
  none: null,
});

/**
 * W4-F2.11, addendum 6 B5: `movedForward` IS PART OF THE DECISION.
 *
 * The rail already says WHERE the learner is standing. This says WHY they are
 * standing there, on the one rung where the two can differ: Check-in is reached
 * either by claiming a task and finishing it, or by the squad's stamp carrying
 * the learner past Plan and Work without one. Once the write-through shortcut
 * has fired, `mission.isTaskCreated` is true on both paths and the two are
 * indistinguishable downstream, so the fact has to leave this function with the
 * decision that produced it rather than be reconstructed by a reader.
 *
 * FALSE EVERYWHERE ELSE, including the credential arm, because the state it
 * names is one rung of one ladder.
 */
const decision = (
  stepId,
  status,
  destinationKind,
  sideEffects,
  movedForward = false,
) => ({
  stepId,
  status,
  destinationKind,
  label: LABEL_BY_DESTINATION[destinationKind] ?? null,
  sideEffects,
  movedForward,
});

const stepDecision = (stepId, status, sideEffects, movedForward = false) =>
  decision(stepId, status, stepId, sideEffects, movedForward);

/**
 * @param {object} inputs
 * @param {boolean} inputs.ready capsule AND capsuleUsers have both hydrated
 * @param {boolean} inputs.usesV4Experience the v4/legacy ladder selector
 * @param {boolean} inputs.isCredentialJourney journey variant is `credential`
 * @param {number|null} inputs.credentialEffectiveStatus deriveCredentialEffectiveStatus output
 * @param {boolean} inputs.credentialMissionOpened clearance act_state.mission_opened
 * @param {boolean} inputs.credentialGatesComplete isCredentialLevelComplete output
 * @param {string|null} inputs.credentialExamState the terminal exam beat's own
 *   state (a BEAT_STATES value), or null when this capsule carries no terminal
 *   exam. Read only on the credential arm, and only once the gates are complete.
 * @param {number} inputs.status the redux status (what the cascade compares against)
 * @param {number|null} inputs.week capsule.week
 * @param {boolean} inputs.hasReadChallenge cohort_user.read_challenge, already coerced
 * @param {boolean} inputs.readObjective capsule_user.read_objective, truthiness
 * @param {boolean} inputs.moduleCompleted last module is_completed
 * @param {boolean} inputs.reflectionPresent last module reflection !== undefined
 * @param {boolean} inputs.taskCreated mission.isTaskCreated
 * @param {boolean} inputs.reportSubmitted capsule_user.milestone_report_submitted
 * @param {boolean} inputs.deliverablesSubmitted capsule_user.deliverables_submitted
 * @param {boolean} inputs.shipWalkthroughRecorded has THIS learner's own ship
 *   event been recorded for THIS capsule, by the SERVER or by the client
 *   ledger? See the Ship gate below. The two sources are resolved into this one
 *   answer by `selectJourneyInputs` (W4-F2.12).
 * @param {boolean} inputs.hasIncompleteOwnTask this learner has a quest not at status 5
 * @param {boolean} inputs.movedForwardRecorded the squad's stamp carried THIS
 *   learner past Plan and Work on THIS capsule, from the client ledger's own
 *   mark. See `movedPastOwnTasks` below.
 * @param {boolean} inputs.hasNextCapsule mission.nextCapsule exists
 * @returns {{stepId: string, status: (number|null), label: (string|null),
 *   destinationKind: string, sideEffects: Array<{type: string, value?: *}>,
 *   movedForward: boolean}}
 */
export function deriveJourneyStep({
  ready = false,
  usesV4Experience = false,
  isCredentialJourney = false,
  credentialEffectiveStatus = null,
  credentialMissionOpened = false,
  credentialGatesComplete = false,
  credentialExamState = null,
  status = 0,
  week = null,
  hasReadChallenge = false,
  readObjective = false,
  moduleCompleted = false,
  reflectionPresent = false,
  taskCreated = false,
  reportSubmitted = false,
  deliverablesSubmitted = false,
  shipWalkthroughRecorded = false,
  hasIncompleteOwnTask = false,
  movedForwardRecorded = false,
  hasNextCapsule = false,
} = {}) {
  const sideEffects = [];
  const setStatusIfChanged = (target) => {
    if (status !== target) {
      sideEffects.push({ type: JOURNEY_EFFECT.SET_STATUS, value: target });
    }
  };

  // The cascade's own guard. Nothing has hydrated, so nothing is decided and
  // nothing is dispatched. NOT an error state: the projection and the journey
  // both render their loading treatments off their own owners.
  if (!ready) return decision(JOURNEY_STEP.NONE, null, "none", sideEffects);

  const settle = (targetStatus) => {
    setStatusIfChanged(targetStatus);
    if (isCredentialJourney) return credentialDecision(sideEffects);
    const ladder = usesV4Experience ? V4_STEP_BY_STATUS : LEGACY_STEP_BY_STATUS;
    const stepId = ladder[targetStatus];
    return stepDecision(
      stepId,
      targetStatus,
      sideEffects,
      stepId === JOURNEY_STEP.CHECKIN && movedPastOwnTasks,
    );
  };

  const credentialDecision = (effects) => {
    // Act 1: the mission has not opened. deriveCredentialEffectiveStatus passes
    // the redux status straight through here because it IS accurate in Act 1,
    // and the ratified copy table has ONE Act-1 state.
    if (!credentialMissionOpened) {
      return stepDecision(
        JOURNEY_STEP.ACT1,
        credentialEffectiveStatus,
        effects,
      );
    }
    if (credentialGatesComplete) {
      // Chip C. The deliverable is in. WHICH settled identity depends on the
      // terminal exam beat, and on nothing else: with the exam in play the
      // learner is standing on the exam node, not on Ship, so this is `exam` and
      // the card renders no per-node sentence. With no exam beat at all, or with
      // one the backend still holds LOCKED, the cursor is on Ship and this is
      // the plain submitted level it has always been.
      if (credentialExamInPlay(credentialExamState)) {
        return stepDecision(
          JOURNEY_STEP.EXAM,
          credentialEffectiveStatus,
          effects,
        );
      }
      return decision(
        JOURNEY_STEP.SUBMITTED,
        credentialEffectiveStatus,
        hasNextCapsule ? "next-level" : "back-to-challenge",
        effects,
      );
    }
    // Act 2 runs the SAME downstream gates as the v4 ladder, so it reads off the
    // v4 map. deriveCredentialEffectiveStatus returns 0..7; anything outside the
    // map falls back to the Act-1 identity rather than inventing a step.
    const stepId =
      V4_STEP_BY_STATUS[credentialEffectiveStatus] ?? JOURNEY_STEP.ACT1;
    return stepDecision(stepId, credentialEffectiveStatus, effects);
  };

  /**
   * IS THIS LEARNER PAST THEIR OWN SHIP STEP? (W4-F2.10, Addendum 5 rule 1.)
   *
   * TWO FACTS, AND UNTIL WAVE 4 B2 ONE COLUMN CARRIED BOTH. The squad's output
   * being in is `capsule_users.deliverables_submitted`; the learner having walked
   * through Ship themselves used to be the same column, because only the
   * uploader was ever stamped. B2's reader floor now stamps every member of the
   * roster snapshot the instant the output passes
   * (`FloorTransition.php:55-60`), and that quietly re-pointed this gate at a
   * fact about somebody else: a squadmate still on their take was read as past
   * Ship, `terminal()` advanced their level with no drawer and no ending, and
   * Wave 3 addendum 1 ruling A1 rules 2 and 3 were undone without a line of
   * frontend code changing. Amir, 2026-09-18: "Even though it was already
   * submitted, the students should still have to click Next to go through the
   * ship step."
   *
   * SO THE GATE ASKS BOTH, AND THE SECOND IS THE LEARNER'S OWN SHIP EVENT.
   * `shipWalkthroughRecorded` is an INPUT and never a storage read taken here:
   * this function is pure over the snapshot it is handed, the golden record
   * depends on that, and a ledger read inside it would be invisible to the
   * selector that memoizes it. `selectJourneyInputs` owns the read; see
   * `store/shipWalkthrough/slice.js` for why the answer has to reach the store
   * at all.
   *
   * THE FAILURE DIRECTION IS DELIBERATE. With no record, the learner is offered
   * their own Ship step again: `Submitted`, Next, the ending, the door. The
   * other direction costs a learner their level ending and drops them on a
   * level they never walked to, which is the defect.
   *
   * AND THE RECORD IS THE SERVER'S FIRST (W4-F2.12, addendum 6 C). Addendum 5
   * disclosed that the only record of the walkthrough was the client ledger, so
   * a second device or a cleared browser replayed a Ship step the learner had
   * walked, and it queued the durable fix as W4-B2.12. That backend has
   * shipped: `capsule_users.ship_acknowledged_at` is stamped on the caller's
   * own row by the write each ship tail already makes, and it is served back on
   * `me/economy.levels[]`. `selectJourneyInputs` reads that stamp and falls
   * back to the ledger, so this constant is unchanged and the cost the ruling
   * disclosed is closed on every cohort the economy read reaches.
   *
   * THE BACKEND'S STAMP IS UNCHANGED, and that is rule 6: the journey head, the
   * economy's level lifecycle and the Vault's hit count all rely on every roster
   * member being stamped. What changes is only what the frontend INFERS from it.
   *
   * AND IT IS THIS GATE ONLY, WHICH IS A RULING AND NOT AN OVERSIGHT (Fable
   * domain review MAJOR 1, Amir 2026-09-19). The write-through shortcut two
   * rungs above asks `deliverablesSubmitted` on its own, so a stamped squadmate
   * with no task of their own is handed one and the next pass moves them off
   * Plan, past Work, to Check-in. The review read that as the same defect one
   * rung lower. It is not: it never reaches `terminal()`, the learner stays
   * inside the level, and Amir wants the skip. What rule 1 protects is the
   * LEVEL boundary, and that boundary is this constant.
   */
  const pastOwnShip = deliverablesSubmitted && shipWalkthroughRecorded;

  /**
   * WAS THIS LEARNER CARRIED PAST PLAN AND WORK BY THE SQUAD'S STAMP? (W4-F2.11,
   * addendum 6 B5, held from addendum 5 rule 1.)
   *
   * THE SKIP ITSELF IS RULED AND STAYS. Amir, 2026-09-19: "I was actually okay
   * with skipping the plan step if the deliverable is already submitted. I like
   * that skip." Planning tasks for an output already turned in is hollow work.
   * What the skip never did was SAY anything: the learner opened their
   * dashboard, found themselves two rungs further along than they left it, and
   * nothing on the surface accounted for the move.
   *
   * IT IS A RECORD OF THE TRANSITION AND NOT A RECONSTRUCTION OF IT (W4-F2.11
   * fix batch, Codex MINOR 1). The chip as built asked three questions of the
   * PRESENT -- the stamp is in, the learner has not walked their own Ship, and
   * they have no task of their own on this capsule -- and inferred a past event
   * from the third. A learner who claimed a task, finished it, walked to
   * Check-in and then had that quest deleted or moved to another capsule loses
   * the third fact and is told the squad carried them, which is a false claim
   * about their own week. The inverse is available too: a quest retained on the
   * capsule suppresses the line for a learner the stamp really did carry.
   *
   * So the carry is MARKED where it happens, by the write-through shortcut two
   * rungs below, and read back here from the client ledger through
   * `selectJourneyInputs`. Nothing else in the product can produce that mark,
   * it survives a reload, and it says the one thing the line claims.
   *
   * AND HERE IS THE LIMIT OF THAT, PLAINLY (W4-F2.11 delta review, MAJOR 1).
   * The mark is a record of a carry the PAGE WATCHED HAPPEN. It is not a record
   * of the learner's whole history on the capsule, because the fact the carry
   * is detected from is itself reconstructed: `GET /users/mission` derives
   * `isTaskCreated` as "does a quest for this capsule exist right now"
   * (`UserAPIController.php`), so a learner whose own quest is deleted is
   * served `isTaskCreated: false` on the next read, reaches the shortcut like
   * any other unplanned learner on a stamped level, and is marked. The mark is
   * then durable, so re-creating a task no longer clears it the way the
   * `hasOwnTask` reconstruction did.
   *
   * WHAT THE PRODUCT THEREFORE CLAIMS, EXACTLY: on a stamped level, a learner
   * who has no quest of their own AT THE MOMENT THE LADDER READS THEM is
   * carried past Plan and Work, and the Check-in line says so. That is true of
   * a learner who never planned and true of a learner whose plan was deleted,
   * and today the platform cannot tell the two apart, because no served fact
   * survives the deletion: not the quest collection, not the client `status`
   * (written only by `setStep`, never hydrated), not `read_objective` or the
   * module and reflection gates, which both learners clear before this rung.
   * Telling them apart needs a DURABLE plan fact from the backend (a stored
   * `is_task_created`, or a `quest_created_at` on `capsule_users`), which is a
   * backend chip and is recorded as a follow-up rather than guessed at here.
   *
   * IT IS READ ONLY AT THE CHECK-IN RUNG, in `settle` above. A learner standing
   * anywhere else is not being told anything about how they got there.
   */
  const movedPastOwnTasks = movedForwardRecorded;

  const terminal = () => {
    if (hasNextCapsule) {
      sideEffects.push({ type: JOURNEY_EFFECT.ADVANCE_CAPSULE });
      // The advance lands in the reducer this same tick; the NEXT pass derives
      // the new level. This pass reports the level it just closed.
      return decision(
        JOURNEY_STEP.SUBMITTED,
        status,
        "next-level",
        sideEffects,
      );
    }
    setStatusIfChanged(7);
    sideEffects.push({ type: JOURNEY_EFFECT.FLAG_LAST_LEVEL, value: true });
    return decision(
      JOURNEY_STEP.SUBMITTED,
      7,
      "back-to-challenge",
      sideEffects,
    );
  };

  if (usesV4Experience) {
    if (week === 1 && !hasReadChallenge) return settle(0);

    const gate1 = v4Gate1Decision({
      isCredentialJourney,
      readObjective,
      missionIsTaskCreated: taskCreated,
    });
    if (gate1.resetTaskCreated) {
      sideEffects.push({ type: JOURNEY_EFFECT.SET_TASK_CREATED, value: false });
    }
    if (gate1.pinStatusOne) setStatusIfChanged(1);
    if (gate1.earlyReturn) {
      if (isCredentialJourney) return credentialDecision(sideEffects);
      return stepDecision(V4_STEP_BY_STATUS[1], 1, sideEffects);
    }

    if (!moduleCompleted) return settle(2);
    if (!reflectionPresent) return settle(3);

    // The shortcut that reads its own stale write. See the header.
    if (deliverablesSubmitted && !taskCreated) {
      sideEffects.push({ type: JOURNEY_EFFECT.SET_TASK_CREATED, value: true });
      /* AND THIS IS THE CARRY. It is the one branch in either ladder that moves
         a learner past Plan and Work on somebody else's stamp, so it is the one
         place that can honestly record it. See `movedPastOwnTasks` above.
         THE MARK IS WRITTEN AT THE CARRY AND SAYS ONLY THAT: `taskCreated` here
         is the server's answer to "is there a quest right now", so a learner
         whose own quest was deleted before the stamp arrives on this branch and
         is marked like any other unplanned learner. The limit is stated in full
         above `movedPastOwnTasks`; closing it needs a durable plan fact. */
      sideEffects.push({ type: JOURNEY_EFFECT.MARK_MOVED_FORWARD });
    }

    if (!taskCreated) return settle(4);
    if (hasIncompleteOwnTask) return settle(5);
    if (!reportSubmitted) return settle(6);
    /* The Ship gate. `pastOwnShip` above is the whole of the reasoning; a
       learner AT Ship settles on 7 whether the output is still out or already in,
       and the drawer states which of the two it is. */
    if (!pastOwnShip) return settle(7);
    return terminal();
  }

  // Legacy ladder (uses_v4_experience = false), preserved exactly.
  if (week === 1 && !hasReadChallenge) return settle(0);
  if (!moduleCompleted) return settle(1);
  if (!reflectionPresent) return settle(2);
  if (!readObjective) {
    if (taskCreated) {
      sideEffects.push({ type: JOURNEY_EFFECT.SET_TASK_CREATED, value: false });
    }
    return settle(3);
  }
  if (deliverablesSubmitted && !taskCreated) {
    sideEffects.push({ type: JOURNEY_EFFECT.SET_TASK_CREATED, value: true });
    /* The SAME carry on the legacy ladder, and it has to be the same one: a
       legacy cohort's squadmate is stamped by the same reader floor. */
    sideEffects.push({ type: JOURNEY_EFFECT.MARK_MOVED_FORWARD });
  }
  if (!taskCreated) return settle(4);
  if (hasIncompleteOwnTask) return settle(5);
  if (!reportSubmitted) return settle(6);
  /* The SAME Ship gate on the legacy ladder, and it has to be the same one: a
     legacy cohort's squadmate is stamped by the same reader floor, so a gate
     applied to v4 alone would leave the defect live on 27 in-flight cohorts. */
  if (!pastOwnShip) return settle(7);
  return terminal();
}

/**
 * stepId -> the index into Dashboard's `stepStatus` array that opens it.
 *
 * `stepStatus` IS the open-handler registry: it is the array Dashboard builds
 * from its six local drawer setters, and every call site
 * (`stepStatus?.[i]?.open?.()`) indexes it by status. This mapping is the seam
 * between the two halves of the ruling: the selector owns the DECISION, the
 * component owns the HANDLERS, and this is how a decision names a handler
 * without either side reaching into the other.
 *
 * Returns null for identities that open nothing: `none` (nothing hydrated),
 * `submitted` (the ratified surface opens no node after submission), `act1`
 * (the credential spine routes Act 1 through its own act-break handoff, not a
 * stepStatus index), and `exam` (the terminal exam node opens through the
 * spine's own beat handler, and only when the beat is active).
 *
 * It returns null for all four by the same mechanism it always has: none of them
 * appears in either ladder, so `indexOf` is -1. No special case was added.
 *
 * @param {string} stepId
 * @param {boolean} usesV4Experience
 * @returns {number|null}
 */
export function journeyStepIndex(stepId, usesV4Experience) {
  const ladder = usesV4Experience ? V4_STEP_BY_STATUS : LEGACY_STEP_BY_STATUS;
  const index = ladder.indexOf(stepId);
  return index === -1 ? null : index;
}

export default deriveJourneyStep;
